// src/pages/services/establishment-compliance.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Building2, Calendar, FileCheck, ShieldCheck } from 'lucide-react';

const EstablishmentCompliance = () => {
  const navigate = useNavigate();

  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 800], [0, -80]); 

  const handleFreeConsultation = () => navigate('/contact');

  const painPoints = [
    "Multiple state-specific rules with different filing deadlines",
    "Difficulty tracking renewals for multi-location businesses",
    "Risk of penalties due to missed registrations or expired licences",
    "Lack of centralised documentation for audits and inspections"
  ];

  const whatWeManage = [
    "New establishment registrations across all states",
    "Annual licence renewals and amendments",
    "Employee register maintenance as per applicable state rules",
    "Display compliance (posters, notices, certifications)",
    "Compliance calendar management with automated reminders",
    "Audit-ready documentation and digital records",
    "Closure and transfer formalities"
  ];

  const industries = [
    "Retail", "IT/ITES", "BFSI", "Hospitality",
    "Healthcare", "E-commerce Fulfilment", "All commercial establishments"
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 overflow-hidden">
      {/* Hero – more luxurious with gradient overlay & parallax */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-emerald-50 via-white to-cyan-50 pt-24 overflow-hidden">
        {/* Animated background elements */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], rotate: [0, 3, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-[900px] h-[900px] bg-emerald-200/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.2, 1], rotate: [0, -4, 0] }}
          transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-40 -right-40 w-[1000px] h-[1000px] bg-cyan-200/25 rounded-full blur-3xl"
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="max-w-5xl"
          >
            <div className="inline-flex items-center gap-4 bg-white/80 backdrop-blur-xl border border-emerald-100/50 px-8 py-4 rounded-full mb-10 shadow-lg">
              <Building2 className="text-[#d97925]" size={28} />
              <span className="text-[#d97925] font-semibold tracking-[0.2em] uppercase text-sm">
                Establishment Compliance
              </span>
            </div>

            <h1 className="text-6xl md:text-[100px] leading-[0.95] font-extrabold tracking-[-0.04em] mb-10">
              Shops &<br />
              <span className="bg-gradient-to-r from-[#d97925] via-orange-600 to-red-500 bg-clip-text text-transparent">
                Establishments
              </span><br />
              Compliance
            </h1>

            <p className="text-2xl md:text-3xl text-zinc-600 max-w-4xl leading-tight">
              Complete registration and licensing for commercial establishments under the Shops & Establishments Act across all states.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-32 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 xl:gap-24">
            {/* Left Column – Text */}
            <div className="lg:col-span-7 space-y-20 lg:space-y-28">
              {/* Overview */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9 }}
              >
                <h2 className="text-5xl font-bold tracking-tight mb-8">Overview</h2>
                <div className="space-y-8 text-[18px] leading-relaxed text-zinc-700">
                  <p>
                    Operating a business across India means navigating the Shops and Establishments Act — a state-level legislation that governs working hours, employee conditions, holidays, and registration requirements for all commercial establishments.
                  </p>
                  <p>
                    Non-compliance can result in fines, licence cancellations, and business disruption. TickBox provides end-to-end management of your establishment compliance — ensuring seamless operations, on-time filings, and complete documentation at every location.
                  </p>
                </div>
              </motion.div>

              {/* Pain Points */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, delay: 0.15 }}
              >
                <h3 className="text-4xl font-semibold mb-8">Common Pain Points</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {painPoints.map((point, i) => (
                    <div
                      key={i}
                      className="bg-zinc-50 p-6 rounded-2xl border border-zinc-100 flex items-start gap-4 hover:border-red-200 transition-colors"
                    >
                      <div className="mt-1.5 w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                      <p className="text-lg leading-relaxed">{point}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* What We Manage */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, delay: 0.3 }}
              >
                <h3 className="text-4xl font-semibold mb-8">What TickBox Manages</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {whatWeManage.map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.06 }}
                      className="bg-emerald-50/40 p-6 rounded-2xl border border-emerald-100 flex items-start gap-4 hover:bg-emerald-50 transition-colors"
                    >
                      <FileCheck className="text-[#d97925] mt-1" size={24} />
                      <p className="text-lg leading-relaxed">{item}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Industries Served */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.9, delay: 0.45 }}
              >
                <h3 className="text-4xl font-semibold mb-8">Industries Served</h3>
                <div className="flex flex-wrap gap-4">
                  {industries.map((industry, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.05 }}
                      className="px-7 py-3.5 bg-emerald-50 text-emerald-700 rounded-2xl text-base font-medium border border-emerald-100 hover:bg-emerald-100 transition-colors"
                    >
                      {industry}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right Column – Visuals with parallax */}
            <motion.div
              style={{ y: imageY }}
              className="lg:col-span-5 lg:sticky lg:top-32 h-fit space-y-10"
            >
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-zinc-200">
                <img
                  src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1200&auto=format&fit=crop&q=80"
                  alt="Modern retail & office establishment"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute bottom-10 left-10 text-white">
                  <p className="text-2xl font-semibold drop-shadow-lg">Multi-state compliant shops & offices</p>
                  <p className="text-base opacity-90 mt-2 drop-shadow">Powered by TickBox</p>
                </div>
              </div>

              {/* Secondary card – Calendar highlight */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-3xl p-10 border border-emerald-100 shadow-xl"
              >
                <div className="flex items-center gap-5 mb-6">
                  <Calendar className="text-[#d97925]" size={48} />
                  <h4 className="text-3xl font-semibold">Automated Compliance Calendar</h4>
                </div>
                <p className="text-lg text-zinc-700 leading-relaxed">
                  Never miss a deadline again. Proactive reminders, centralised tracking, and renewal alerts for every location — all in one dashboard.
                </p>
              </motion.div>

              {/* Tertiary – Shield icon block */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="bg-white rounded-3xl p-10 border border-emerald-100 shadow-lg flex items-start gap-6"
              >
                <ShieldCheck className="text-[#d97925] mt-1" size={48} />
                <div>
                  <h4 className="text-3xl font-semibold mb-4">Zero-Risk Assurance</h4>
                  <p className="text-lg text-zinc-700">
                    Audit-ready documentation, digital records, and proactive gap closure — so you stay protected from penalties and disruptions.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Final CTA – full-width gradient */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-32 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white rounded-3xl py-20 px-8 text-center shadow-2xl"
          >
            <h3 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Ready to make your establishments 100% compliant?
            </h3>
            <p className="text-2xl mb-12 max-w-3xl mx-auto opacity-90">
              Let TickBox take over registrations, renewals, audits, and documentation — so you can focus on growth, not compliance worries.
            </p>

            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 25px 50px -12px rgba(16, 185, 129, 0.4)" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleFreeConsultation}
              className="inline-flex items-center gap-4 px-16 py-8 bg-white text-[#d97925] font-semibold text-2xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-colors"
            >
              Get Establishment Compliance Support
              <ArrowRight size={32} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EstablishmentCompliance;